/**
 * @file MyTodosPage.tsx
 * @brief `/app/todos` and `/company/todos`: every to-do from the user's
 *        meetings, in one list.
 *
 * @details
 * The phone's "My to-dos" screen (DES 12-10-2026), kept small for v1:
 * - Pill tabs: Open N · Overdue N · Done (`status`).
 * - One filter menu (14-10-2026): Group by date / meeting + a due-date filter;
 *   "Everyone's" removed (was `scope`). Before: a ⋯-style menu with SearchableMenuList,
 *   never a dialog (webpanel-searchable-menu-list).
 * - A search field (`q`, debounced) in the bottom dock, as Notes / Boardrooms.
 * - The grouped rows (MyTodosList), up to 300 (`limit=300`).
 * - Empty: "To-dos from your meetings will appear here" for a user with no
 *   to-dos at all; "All caught up" on Open with nothing left.
 *
 * Tab, scope and search live in the URL, so Back from a meeting returns to
 * the same list. Not in v1, on purpose: create, edit, a date filter.
 *
 * @author DES
 * @date 12-10-2026
 * @updated 13-10-2026 — DES: full width, search in the bottom dock like the other pages.
 */

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useBoardroomBasePath } from "@/features/employee-boardroom/useBoardroomBasePath";
import {
  AlarmClock,
  CalendarDays,
  ChevronDown,
  Circle,
  CircleCheck,
  ListFilter,
  Tag,
  UserRound,
} from "lucide-react";
import { DUE_FILTER_LABEL, filterByDue, type DueFilter, type TodoGrouping } from "./myTodosLogic";
import {
  TODO_VIEW_LABELS,
  VIEWS_FOR,
  readRememberedView,
  rememberView,
  resolveView,
  type TodoViewKey,
} from "./todoViews";
import type { MyTodoItem } from "./myTodosApi";
import { BoardroomTable } from "@/components/icons/BoardroomTable";
import { EmptyState } from "@/components/feedback/EmptyState";
import { FormSheetTabs, type FormSheetTab } from "@/components/ui/FormSheetTabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/DropdownMenu";
import { SearchableMenuList } from "@/components/ui/SearchableMenuList";
import { ExpandingSearch } from "@/components/ui/ExpandingSearch";
import { cn } from "@/shared/theme/cn";
import { dockFadeBehind } from "@/shared/theme/dockGlass";
import { MyTodosList } from "./MyTodosList";
import { useMyTodos, type MyTodosScope, type MyTodosStatus } from "./myTodosApi";

const PAGE_LIMIT = 300;
const SEARCH_DEBOUNCE_MS = 300;

/** View → the list's grouping (F1a: By due date = the server's date groups). */
const GROUPING_OF: Record<string, TodoGrouping> = { due: "date", meeting: "meeting", category: "category", person: "person" };
const VIEW_ICON: Partial<Record<TodoViewKey, typeof Tag>> = { due: CalendarDays, category: Tag, person: UserRound };

function asStatus(value: string | null): MyTodosStatus {
  return value === "overdue" || value === "done" ? value : "open";
}

export function MyTodosPage() {
  const BASE = useBoardroomBasePath();
  const [params, setParams] = useSearchParams();
  const status = asStatus(params.get("status"));
  // Mine only (DES 14-10-2026: "remove everyone"); another person's to-dos
  // are in that meeting's To-dos tab.
  const scope: MyTodosScope = "mine";
  const [dueFilter, setDueFilter] = useState<DueFilter>("any");
  // By due date / By category / By person / By meeting (DES 14-10-2026,
  // F1a 31-10-2026), remembered on this browser; none picked = the smart
  // default (todoViews.ts).
  const [picked, setPicked] = useState<TodoViewKey | null>(() => readRememberedView("myTodos"));
  const urlQ = params.get("q") ?? "";
  const [search, setSearch] = useState(urlQ);
  const [scopeOpen, setScopeOpen] = useState(false);

  const setParam = (key: string, value: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  // The field types freely; the URL (and the request) follows after a pause.
  useEffect(() => {
    if (search.trim() === urlQ) return;
    const t = window.setTimeout(() => setParam("q", search.trim() || null), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, urlQ]);

  const list = useMyTodos({ status, scope, q: urlQ || undefined, limit: PAGE_LIMIT });
  const counts = list.data?.counts;
  const allItems: MyTodoItem[] = list.data?.groups.flatMap((g) => g.items) ?? [];
  const labelled = allItems.some((it) => it.category);
  const viewFacts = (it: MyTodoItem) => ({ category: it.category, categoryId: it.categoryId });
  const view = resolveView("myTodos", picked, allItems, viewFacts);
  const grouping: TodoGrouping = GROUPING_OF[view] ?? "date";
  const viewOptions = VIEWS_FOR.myTodos.filter((v) => v !== "category" || labelled);

  const tabs: FormSheetTab<MyTodosStatus>[] = [
    { id: "open", label: counts ? `Open ${counts.open}` : "Open", icon: Circle },
    { id: "overdue", label: counts ? `Overdue ${counts.overdue}` : "Overdue", icon: AlarmClock },
    { id: "done", label: "Done", icon: CircleCheck },
  ];

  // Done is when it was ticked, not due: the filter is for open work.
  const effectiveDue: DueFilter = status === "done" ? "any" : dueFilter;
  const shown = list.data
    ? { ...list.data, groups: filterByDue(list.data.groups, effectiveDue, list.data.today) }
    : undefined;
  const rows = shown?.groups.reduce((n, g) => n + g.items.length, 0) ?? 0;
  const firstTime = Boolean(counts) && counts!.open + counts!.done === 0 && !urlQ;

  return (
    // Full width, title + subtitle top left and the search in the bottom
    // dock, as Notes and Boardrooms (DES 13-10-2026: "baki pages jaisi").
    <div className="flex min-h-[calc(100dvh-5rem)] w-full flex-col px-5 pb-6 pt-6 md:px-8">
      <div className="min-w-0">
        <h1 className="text-[28px] font-bold leading-9 tracking-tight">My to-dos</h1>
        <p className="mt-1 text-[15px] text-muted-foreground">From all your meetings</p>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <FormSheetTabs<MyTodosStatus>
          compact
          tone="phone"
          label="To-do status"
          tabs={tabs}
          value={status}
          onChange={(next) => setParam("status", next === "open" ? null : next)}
        />

        {/* One filter menu (DES 14-10-2026): group by date / meeting and a
            due-date filter; Everyone's removed. A menu, never a dialog. */}
        <DropdownMenu open={scopeOpen} onOpenChange={setScopeOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Filter: ${TODO_VIEW_LABELS[view].toLowerCase()}, ${DUE_FILTER_LABEL[dueFilter]}`}
              className={cn(
                "inline-flex h-[50px] items-center gap-2 rounded-full px-5 text-[15px] font-semibold transition-colors",
                dueFilter === "any"
                  ? "bg-[#ECEBE9] text-app-text hover:bg-[#E2E0DD] dark:bg-app-quaternary dark:hover:bg-white/10"
                  : "bg-brand text-white hover:bg-brand/90",
              )}
            >
              <ListFilter className="h-4 w-4" strokeWidth={1.75} />
              {dueFilter === "any" ? TODO_VIEW_LABELS[view] : DUE_FILTER_LABEL[dueFilter]}
              <ChevronDown className="h-4 w-4 opacity-60" strokeWidth={1.75} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64 p-1.5">
            <SearchableMenuList
              title="Group by"
              items={viewOptions.map((v) => {
                const Icon = VIEW_ICON[v];
                return {
                  value: v,
                  label: TODO_VIEW_LABELS[v],
                  icon: Icon ? (
                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                  ) : (
                    <BoardroomTable className="h-4 w-4" strokeWidth={1.75} />
                  ),
                };
              })}
              selected={[view]}
              onSelect={(value) => {
                const next = value as TodoViewKey;
                setPicked(next);
                rememberView("myTodos", next);
                setScopeOpen(false);
              }}
            />
            <div className="my-1.5 h-px bg-border" />
            <SearchableMenuList
              title="Due"
              items={(Object.keys(DUE_FILTER_LABEL) as DueFilter[]).map((k) => ({
                value: k,
                label: DUE_FILTER_LABEL[k],
              }))}
              selected={[dueFilter]}
              onSelect={(value) => {
                setDueFilter(value as DueFilter);
                setScopeOpen(false);
              }}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mt-6 flex-1">
        {list.isLoading ? null : list.error && !list.data ? (
          <div className="mt-10">
            <p className="text-[15px] text-muted-foreground">Could not load your to-dos.</p>
            <button
              type="button"
              className="mt-2 text-sm font-semibold text-brand"
              onClick={() => void list.refetch()}
            >
              Try again
            </button>
          </div>
        ) : !list.data || rows === 0 ? (
          <EmptyState
            shape="row"
            animate={!urlQ}
            title={
              effectiveDue !== "any"
                ? "Nothing due then"
                : urlQ
                ? "No matches"
                : firstTime
                  ? "To-dos from your meetings will appear here"
                  : status === "open"
                    ? "All caught up"
                    : status === "overdue"
                      ? "Nothing overdue"
                      : "Nothing done yet"
            }
            description={
              effectiveDue !== "any"
                ? `No to-dos for "${DUE_FILTER_LABEL[effectiveDue]}".`
                : urlQ
                ? "No to-dos match your search."
                : firstTime || status === "open"
                  ? undefined
                  : status === "overdue"
                    ? "No to-do is past its due date."
                    : "To-dos you tick will appear here."
            }
          />
        ) : (
          <>
            <MyTodosList
              data={shown ?? list.data}
              meetingHref={(id) => `${BASE}/meetings/${id}`}
              backLabel="My to-dos"
              // Done stays one list.
              grouping={status === "done" ? "date" : grouping}
              collapsible
            />
            {list.data.truncated ? (
              <p className="mt-6 text-center text-[13px] text-muted-foreground">
                Showing the first {PAGE_LIMIT}. Search to find the rest.
              </p>
            ) : null}
          </>
        )}
      </div>

      {/* Dock: the shared page search, a circle that opens on press like
          Notes' (DES 13-10-2026); THE dock fade. */}
      <div className={cn("pointer-events-none sticky bottom-6 z-20 mt-auto flex justify-center pt-8", dockFadeBehind)}>
        <ExpandingSearch
          value={search}
          onChange={setSearch}
          placeholder="Search to-dos"
          label="Search to-dos"
          className="pointer-events-auto"
        />
      </div>
    </div>
  );
}

export default MyTodosPage;
